/**
 * Internal scope classifier — produces a "Size - Complexity" label from the
 * sizing + complexity-flag questions asked inside each Target-Aware modal.
 *
 *   Size         = Small | Medium | Large | TBC
 *   Complexity   = Simple | Complex | TBC
 *
 * Combined label looks like "Medium - Complex", "Small - Simple", etc.
 *
 * IMPORTANT: this classification is INTERNAL. It is written into
 * `scope.details.__classification` for downstream consumers (pricing engine,
 * scope-summary sales view, SOW generator) but MUST NOT be rendered anywhere
 * customer-facing. Clients see only the questions, never the computed label —
 * showing it would invite pricing arguments before CTB has reviewed the data.
 *
 * Universal contract:
 *   size_score 1 = Small
 *   size_score 2 = Medium
 *   size_score 3 = Large
 *
 *   complexity_score 0–{threshold-1} = Simple
 *   complexity_score >= threshold     = Complex
 *
 *   threshold = 2 for high-risk types (Cloud Pentest, Internal Network, AI/LLM)
 *   threshold = 3 for everything else
 *
 * When the customer answers "Not sure" to a sizing question, size becomes TBC
 * (engine widens the price range — see pricingCalculator.ts).
 */

import type { AssessmentType } from './questionnaires';

export type SizeBand = 'Small' | 'Medium' | 'Large' | 'TBC';
export type ComplexityBand = 'Simple' | 'Complex' | 'TBC';

export type ScopeClassification = {
  size: SizeBand;
  complexity: ComplexityBand;
  /** "Medium - Complex" — for display in sales/internal views only. */
  label: string;
  /** Human-readable reasons used to populate the size band. */
  sizeReasons: string[];
  /** Human-readable complexity flags that fired. */
  complexityFlags: string[];
};

/** Types where the complexity threshold is lowered to 2 flags. */
const HIGH_RISK_TYPES: ReadonlySet<AssessmentType> = new Set([
  'CLOUD_PENTEST',
  'INTERNAL_NETWORK',
  'AI_LLM',
]);

/** Public entry point. Returns null when the answers blob is empty (modal not
 *  yet filled). Otherwise always returns a classification — TBC bands when
 *  the answers don't pin a value. */
export function classifyScope(
  type: AssessmentType,
  d: Record<string, unknown>,
): ScopeClassification | null {
  if (!d || Object.keys(d).length === 0) return null;

  const { size, sizeReasons } = scoreSize(type, d);
  const { flags } = scoreComplexity(type, d);
  const threshold = HIGH_RISK_TYPES.has(type) ? 2 : 3;
  const complexity: ComplexityBand =
    flags.length >= threshold ? 'Complex' : 'Simple';

  // Cloud Pentest carries a default-complex bias: per the playbook, unless
  // scope is explicitly limited (no IAM privesc, no K8s, no provider approval)
  // we treat it as Complex even if formal flag count is low.
  const cloudPentestForced =
    type === 'CLOUD_PENTEST' && complexity === 'Simple' && !isLimitedCloudPentest(d);

  return {
    size,
    complexity: cloudPentestForced ? 'Complex' : complexity,
    label: `${size} - ${cloudPentestForced ? 'Complex' : complexity}`,
    sizeReasons,
    complexityFlags: cloudPentestForced
      ? [...flags, 'Cloud pentest defaults to Complex unless clearly limited']
      : flags,
  };
}

// ─── helpers ────────────────────────────────────────────────────────────────

function asStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}
function asArr(v: unknown): string[] {
  return Array.isArray(v) ? (v.filter((x) => typeof x === 'string') as string[]) : [];
}
function asBool(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined;
}

// ─── sizing ─────────────────────────────────────────────────────────────────

function scoreSize(
  type: AssessmentType,
  d: Record<string, unknown>,
): { size: SizeBand; sizeReasons: string[] } {
  const reasons: string[] = [];
  const mark = (label: string, band: SizeBand): { size: SizeBand; sizeReasons: string[] } => {
    reasons.push(label);
    return { size: band, sizeReasons: reasons };
  };

  switch (type) {
    case 'WEB_APP': {
      const v = asStr(d.pages_count_band);
      if (v === '1_10') return mark('1–10 pages', 'Small');
      if (v === '11_30') return mark('11–30 pages', 'Medium');
      if (v === '31_75') return mark('31–75 pages', 'Large');
      if (v === '75_plus') return mark('75+ pages', 'Large');
      return mark('Page count TBC', 'TBC');
    }
    case 'API': {
      const v = asStr(d.endpoints_count_band);
      if (v === '1_20') return mark('1–20 endpoints', 'Small');
      if (v === '21_100') return mark('21–100 endpoints', 'Medium');
      if (v === '101_300') return mark('101–300 endpoints', 'Large');
      if (v === '300_plus') return mark('300+ endpoints', 'Large');
      return mark('Endpoint count TBC', 'TBC');
    }
    case 'MOBILE_APP': {
      const platforms = asArr(d.platforms);
      const flows = asStr(d.app_flows_count);
      const both = platforms.includes('ios') && platforms.includes('android');
      if (!flows) return mark('Flow count TBC', 'TBC');
      if (flows === '1_5' && !both) return mark('One platform, 1–5 flows', 'Small');
      if (flows === '16_plus' || (both && flows !== '1_5')) {
        return mark(both ? 'Both platforms, many flows' : '16+ flows', 'Large');
      }
      return mark(both ? 'Both platforms, small flow count' : '6–15 flows', 'Medium');
    }
    case 'EXTERNAL_NETWORK': {
      const v = asStr(d.live_hosts_count_band);
      if (v === '1_5') return mark('1–5 live hosts', 'Small');
      if (v === '6_25') return mark('6–25 live hosts', 'Medium');
      if (v === '26_100') return mark('26–100 live hosts', 'Large');
      if (v === '100_plus') return mark('100+ live hosts', 'Large');
      return mark('Live-host count TBC', 'TBC');
    }
    case 'INTERNAL_NETWORK': {
      const v = asStr(d.hosts_count_band);
      if (v === '1_50') return mark('1–50 internal hosts', 'Small');
      if (v === '51_250') return mark('51–250 internal hosts', 'Medium');
      if (v === '251_1000') return mark('251–1000 internal hosts', 'Large');
      if (v === '1000_plus') return mark('1000+ internal hosts', 'Large');
      return mark('Internal-host count TBC', 'TBC');
    }
    case 'CLOUD_CONFIG':
    case 'CLOUD_PENTEST': {
      const v = asStr(d.accounts_count_band);
      if (v === '1') return mark('1 account', 'Small');
      if (v === '2_5') return mark('2–5 accounts', 'Medium');
      if (v === '6_20') return mark('6–20 accounts', 'Large');
      if (v === '20_plus') return mark('20+ accounts', 'Large');
      return mark('Account count TBC', 'TBC');
    }
    case 'M365_REVIEW': {
      const tenants = asStr(d.tenants_count);
      const users = asStr(d.user_count_band);
      const tenantsHigh = tenants === '4_plus';
      const usersLarge = users === '500_plus';
      const usersMid = users === '101_500';
      if (!tenants && !users) return mark('Sizing TBC', 'TBC');
      if (tenantsHigh || usersLarge) return mark(tenantsHigh ? '4+ tenants' : '500+ users', 'Large');
      if (tenants === '2_3' || usersMid) return mark(tenants === '2_3' ? '2–3 tenants' : '101–500 users', 'Medium');
      return mark('1 tenant, ≤100 users', 'Small');
    }
    case 'VPN_FIREWALL_REVIEW': {
      const vpn = asStr(d.vpn_gateways_count);
      const fw = asStr(d.firewall_devices_count);
      const rules = asStr(d.ruleset_size);
      const big = vpn === '6_plus' || fw === '6_plus' || rules === '500_plus';
      const mid = vpn === '2_5' || fw === '2_5' || rules === '101_500';
      if (!vpn && !fw && !rules) return mark('Device count TBC', 'TBC');
      if (big) return mark('6+ devices or 500+ rules', 'Large');
      if (mid) return mark('2–5 devices or 101–500 rules', 'Medium');
      return mark('1 device, ≤100 rules', 'Small');
    }
    case 'AI_LLM': {
      const useCase = asStr(d.use_case_band);
      const sources = asStr(d.data_sources);
      const actions = asStr(d.actions_capability);
      const agentic = actions === 'updates_data' || useCase === 'agent';
      if (agentic) return mark('Agentic — can take actions', 'Large');
      if (useCase === 'rag' || sources === 'internal_docs' || sources === 'sensitive_data' || useCase === 'api_workflow') {
        return mark('Multiple data sources or API workflow', 'Medium');
      }
      if (useCase || sources || actions) return mark('Single chatbot / single workflow', 'Small');
      return mark('AI capability TBC', 'TBC');
    }
    case 'CODE_REVIEW': {
      const v = asStr(d.code_size_band);
      if (v === 'lte_50k') return mark('≤50K LOC', 'Small');
      if (v === '50k_200k') return mark('50K–200K LOC', 'Medium');
      if (v === '200k_plus') return mark('200K+ LOC', 'Large');
      return mark('Code size TBC', 'TBC');
    }
  }
}

// ─── complexity flags ───────────────────────────────────────────────────────

function scoreComplexity(
  type: AssessmentType,
  d: Record<string, unknown>,
): { flags: string[] } {
  const flags: string[] = [];
  const add = (label: string) => flags.push(label);

  // Production-environment flag fires across multiple types.
  const env = asStr(d.environment);
  const isProd = env === 'PRODUCTION';

  switch (type) {
    case 'WEB_APP': {
      const access = asStr(d.access_type);
      const flows = asArr(d.important_flows);
      const roles = asStr(d.user_roles);
      if (d.login_required === true) add('Login / authenticated paths');
      if (access === 'admin' || access === 'multiple_roles') add('Admin or multi-role access');
      if (roles === '2_3' || roles === '4_plus') add('Multiple user roles');
      if (flows.includes('payment')) add('Payment flows');
      if (flows.includes('file_upload')) add('File upload');
      if (flows.includes('admin_panel')) add('Admin panel in scope');
      if (isProd) add('Production testing');
      break;
    }
    case 'API': {
      const docType = asStr(d.doc_type);
      const access = asStr(d.access_type);
      const apiType = asStr(d.api_type);
      if (docType === 'none') add('No API documentation');
      if (access === 'oauth_client' || access === 'mtls') add('OAuth / mTLS authentication');
      if (apiType === 'graphql' || apiType === 'grpc' || apiType === 'soap') {
        add('Non-REST protocol (GraphQL/gRPC/SOAP)');
      }
      if (d.rate_limit_allowed === true) add('Rate-limit testing in scope');
      if (d.destructive_excluded === false) add('Destructive operations in scope');
      if (isProd) add('Production testing');
      break;
    }
    case 'MOBILE_APP': {
      const platforms = asArr(d.platforms);
      if (platforms.includes('ios') && platforms.includes('android')) add('Both iOS and Android');
      if (d.backend_in_scope === true) add('Backend API included');
      if (d.sensitive_local_storage === true) add('Sensitive local storage');
      if (d.biometrics_in_scope === true) add('Biometric authentication');
      if (d.reverse_engineering_allowed === true) add('Reverse-engineering allowed');
      if (d.rooted_devices_allowed === true) add('Rooted / jailbroken testing');
      break;
    }
    case 'EXTERNAL_NETWORK': {
      const services = asArr(d.services_in_scope);
      if (services.length > 1 && !services.includes('tbc')) add('Mixed service surface');
      if (services.includes('vpn')) add('VPN / remote-access exposed');
      if (services.includes('database')) add('Externally-exposed databases');
      if (isProd) add('Production testing');
      break;
    }
    case 'INTERNAL_NETWORK': {
      const depth = asArr(d.testing_depth);
      if (d.ad_in_scope === true) add('Active Directory in scope');
      if (depth.includes('privilege_escalation')) add('Privilege escalation');
      if (depth.includes('lateral_movement')) add('Lateral movement');
      if (d.password_spraying_allowed === true) add('Password spraying allowed');
      if (isProd) add('Production servers included');
      break;
    }
    case 'CLOUD_CONFIG': {
      const provider = asStr(d.provider);
      const review = asStr(d.review_scope);
      if (provider === 'multi') add('Multi-cloud');
      if (d.kubernetes_in_scope === true) add('Kubernetes in scope');
      if (d.storage_buckets_in_scope === true) add('Storage buckets in scope');
      if (review === 'config_plus_iam' || review === 'full_posture') add('IAM included in review');
      break;
    }
    case 'CLOUD_PENTEST': {
      const provider = asStr(d.provider);
      if (provider === 'multi') add('Multi-cloud');
      if (d.iam_privesc_in_scope === true) add('IAM privilege-escalation paths');
      if (d.kubernetes_in_scope === true) add('Kubernetes in scope');
      if (d.provider_approval_needed === true) add('Cloud-provider approval required');
      break;
    }
    case 'M365_REVIEW': {
      const scope = asStr(d.scope_breadth);
      const tenants = asStr(d.tenants_count);
      if (scope === 'full_m365' || scope === 'full_plus_entra') add('Full M365 review');
      if (scope === 'full_plus_entra') add('Entra ID included');
      if (d.external_sharing_enabled === true) add('External sharing enabled');
      if (tenants === '2_3' || tenants === '4_plus') add('Multiple tenants');
      break;
    }
    case 'VPN_FIREWALL_REVIEW': {
      const auth = asArr(d.auth_methods);
      const rules = asStr(d.ruleset_size);
      const device = asStr(d.device_type);
      if (device !== 'firewall') {
        if (auth.length > 0 && !auth.includes('mfa')) add('MFA not enforced');
        if (auth.length > 1) add('Multiple VPN auth methods');
        if (auth.includes('sso')) add('SSO / SAML / OIDC');
      }
      if (rules === '500_plus') add('Large rulebase');
      if (d.active_vpn_testing_allowed === true) add('Active VPN testing allowed');
      break;
    }
    case 'AI_LLM': {
      const sources = asStr(d.data_sources);
      const actions = asStr(d.actions_capability);
      if (d.rag_in_scope === true) add('RAG / retrieval pipeline');
      if (sources === 'sensitive_data') add('Sensitive-data access');
      if (d.tools_called === true || actions === 'calls_tools' || actions === 'updates_data') {
        add('AI calls tools / external APIs');
      }
      if (actions === 'updates_data') add('AI takes write actions');
      if (d.jailbreak_in_scope === true) add('Prompt-injection / jailbreak testing');
      if (d.customer_facing === true) add('Customer-facing AI');
      break;
    }
    case 'CODE_REVIEW': {
      const langs = asArr(d.languages);
      const boundary = asStr(d.review_boundary);
      if (langs.length >= 2) add('Multiple languages');
      if (boundary === 'whole_repo' && asStr(d.code_size_band) === '200k_plus') add('Monorepo-scale review');
      if (d.no_build_docs === true) add('No build / setup documentation');
      const focus = asStr(d.focus_areas) ?? '';
      if (/auth|payment|session|upload/i.test(focus)) add('Auth / payment / session in scope');
      break;
    }
  }
  return { flags };
}

function isLimitedCloudPentest(d: Record<string, unknown>): boolean {
  // "Clearly limited" = no IAM privesc, no K8s, no provider approval needed.
  return (
    asBool(d.iam_privesc_in_scope) === false &&
    asBool(d.kubernetes_in_scope) === false &&
    asBool(d.provider_approval_needed) === false
  );
}
